import { CircleX } from 'lucide-react'
import type { ReactNode } from 'react'

import './resourceTile.css'

/**
 * Плитка источника — один вид для всех разделов с выбором источника (РМК, Чеки, Цены, Поставки…). Верх: метка вида и слово
 * вида, имя крупно; сводка; внизу — действие, прижатое к низу плитки, поэтому плитки одной строки выровнены. О связи
 * плитка говорит только красной меткой «Нет связи», когда источник не отвечает (RP-UI-LINK, шаг 3): что именно, когда
 * и что делать — в точке «Связь» шапки. Метка — значок, слово и рамка, цвет только усиливает (CLAUDE.md §7).
 * Приглушённая плитка (`muted`) — источник выключен: штриховая рамка и бледное имя; сам факт всегда сказан словами в
 * сводке или внизу плитки, рамка только усиливает. Компонент знает только про внешний вид: слова и действие приходят снаружи.
 */
export function ResourceTile({
  mark,
  noun,
  name,
  tone = null,
  down = false,
  muted = false,
  lines = [],
  footer,
  ariaLabel,
}: {
  /** Метка вида: «AQSI», «1С». */
  mark: string
  /** Слово вида: «Кабинет AQSI». */
  noun: string
  name: string
  /** Цвет вида источника (палитра `--krp-source-<tone>`): кромка плитки и метка; `null` — нейтральная плитка. */
  tone?: string | null
  /** Источник не отвечает — красная метка «Нет связи». */
  down?: boolean
  /** Источник выключен: плитка приглушена (штриховая рамка), а слово «выключен» говорит `footer` или `lines`. */
  muted?: boolean
  /** Сводка: «Открыто смен: 2», «Разделы: Чеки · Инциденты». */
  lines?: readonly string[]
  /** Действие плитки (кнопка с записью реестра действий рисуется разделом). */
  footer?: ReactNode
  ariaLabel?: string
}) {
  return (
    <li
      className="krp-resource-tile"
      aria-label={ariaLabel ?? `${noun} ${name}`}
      {...(tone === null ? {} : { 'data-source': tone })}
      {...(down ? { 'data-down': 'true' } : {})} {...(muted ? { 'data-muted': 'true' } : {})}>
      <header className="krp-resource-tile__head">
        <span className="krp-resource-tile__mark" aria-hidden="true">
          {mark}
        </span>
        <span className="krp-resource-tile__who">
          <span className="krp-resource-tile__noun">{noun}</span>
          <strong className="krp-resource-tile__name">{name}</strong>
        </span>
        {down && (
          <span className="krp-resource-tile__down">
            <CircleX size={14} aria-hidden="true" />
            <span>Нет связи</span>
          </span>
        )}
      </header>
      {lines.length > 0 && (
        <ul className="krp-resource-tile__lines">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      {footer != null && <div className="krp-resource-tile__footer">{footer}</div>}
    </li>
  )
}

/** Сетка плиток: одинаковая ширина и высота ячеек, плитки одной строки тянутся до самой высокой. */
export function ResourceTileGrid({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <ul className="krp-resource-tiles" {...(label === undefined ? {} : { 'aria-label': label })}>
      {children}
    </ul>
  )
}
